import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Admin.css';

export default function AdminManagement() {
  const { api } = useContext(AuthContext);

  const [admins, setAdmins]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [saving, setSaving]       = useState(false);

  const [formData, setFormData] = useState({
    id: null, name: '', email: '',
    phone: '', password: '', address: '', city: '',
  });

  useEffect(() => { fetchAdmins(); }, []);

  const fetchAdmins = async () => {
    try {
      setError('');
      // ✅ FIXED: /user/admins (not /users/admins)
      const response = await api.get('/user/admins');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.admins || response.data?.data || []);
      setAdmins(data);
    } catch (err) {
      console.error('Fetch admins error:', err);
      setError('Failed to load admins');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchAdmins(); };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({ id: null, name: '', email: '', phone: '', password: '', address: '', city: '' });
    setModalOpen(true);
  };

  const openEditModal = (admin) => {
    setModalMode('edit');
    setFormData({
      id:       admin.id,
      name:     admin.name    || '',
      email:    admin.email   || '',
      phone:    admin.phone   || '',
      password: '',
      address:  admin.address || '',
      city:     admin.city    || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => { if (saving) return; setModalOpen(false); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.name.trim())  return 'Name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (modalMode === 'add') {
      if (!formData.password.trim())           return 'Password is required';
      if (formData.password.trim().length < 6) return 'Password must be at least 6 characters';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { alert(err); return; }

    setSaving(true);
    try {
      if (modalMode === 'add') {
        await api.post('/auth/register', {
          name:     formData.name.trim(),
          email:    formData.email.trim(),
          phone:    formData.phone.trim()   || null,
          password: formData.password.trim(),
          role:     'admin',
          address:  formData.address.trim() || null,
          city:     formData.city.trim()    || null,
        });
        alert('Admin added successfully!');
      } else {
        // ✅ FIXED: /user/:id (not /users/:id)
        const payload = {
          name:    formData.name.trim(),
          email:   formData.email.trim(),
          phone:   formData.phone.trim()   || null,
          address: formData.address.trim() || null,
          city:    formData.city.trim()    || null,
        };
        if (formData.password.trim()) payload.password = formData.password.trim();
        await api.put(`/user/${formData.id}`, payload);
        alert('Admin updated successfully!');
      }

      setModalOpen(false);
      fetchAdmins();
    } catch (err) {
      console.error('Save admin error:', err);
      alert(err.response?.data?.message || 'Failed to save admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin "${admin.name}"? This cannot be undone.`)) return;
    try {
      // ✅ FIXED: /user/:id (not /users/:id)
      await api.delete(`/user/${admin.id}`);
      setAdmins(prev => prev.filter(a => a.id !== admin.id));
      alert('Admin deleted successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  if (loading) return <LoadingSpinner message="Loading admins..." />;

  return (
    <div className="admin-rider-management">
      <div className="container">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <span className="title-icon">👑</span>
              Admin Management
            </h1>
            <p className="page-subtitle">Total Admins: {admins.length}</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-outline btn-sm" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
            <button className="btn btn-primary" onClick={openAddModal}>
              ➕ Add Admin
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* Empty state */}
        {admins.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👑</div>
            <h2 className="empty-state-title">No admins found</h2>
            <p className="empty-state-text">Add your first admin</p>
            <button className="btn btn-primary" onClick={openAddModal}>➕ Add Admin</button>
          </div>
        ) : (
          <div className="riders-grid">
            {admins.map((admin) => (
              <div key={admin.id} className="rider-card">

                <div className="rider-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="rider-avatar" style={{ background: '#2d6a4f', color: '#fff' }}>
                      {admin.name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div className="rider-info">
                      <h3 className="rider-name">{admin.name}</h3>
                      <p className="rider-email">{admin.email}</p>
                    </div>
                  </div>
                  <span className="status-badge active">👑 Admin</span>
                </div>

                <div className="rider-details">
                  <div className="detail-row">
                    <span className="detail-label">📞 Phone:</span>
                    <span className="detail-value">{admin.phone || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📍 Address:</span>
                    <span className="detail-value">{admin.address || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">🏙️ City:</span>
                    <span className="detail-value">{admin.city || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">🆔 ID:</span>
                    <span className="detail-value">{admin.id}</span>
                  </div>
                </div>

                <div className="rider-actions">
                  <button className="btn btn-sm btn-outline" onClick={() => openEditModal(admin)}>
                    ✏️ Edit
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(admin)}>
                    🗑️ Delete
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>

            <div className="modal-header">
              <h2 className="modal-title">
                {modalMode === 'add' ? '➕ Add Admin' : '✏️ Edit Admin'}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input id="name" name="name" type="text"
                  value={formData.name} onChange={handleChange}
                  placeholder="Admin name" disabled={saving} required />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input id="email" name="email" type="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="admin@email.com" disabled={saving} required />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input id="phone" name="phone" type="tel"
                  value={formData.phone} onChange={handleChange}
                  placeholder="03XX-XXXXXXX" disabled={saving} />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  Password {modalMode === 'add' ? '*' : '(optional)'}
                </label>
                <input id="password" name="password" type="password"
                  value={formData.password} onChange={handleChange}
                  placeholder={modalMode === 'add' ? 'Minimum 6 characters' : 'Leave empty to keep same'}
                  disabled={saving} required={modalMode === 'add'} />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input id="address" name="address" type="text"
                  value={formData.address} onChange={handleChange}
                  placeholder="Address" disabled={saving} />
              </div>

              <div className="form-group">
                <label htmlFor="city">City</label>
                <input id="city" name="city" type="text"
                  value={formData.city} onChange={handleChange}
                  placeholder="City" disabled={saving} />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary"
                  onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit"
                  className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
                  disabled={saving}>
                  {saving
                    ? <><span className="spinner-small"></span> Saving...</>
                    : modalMode === 'add' ? 'Add Admin' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}